import { animated, useSpring } from "@react-spring/web";
import React, { useEffect } from "react";

// components
import Text, { BaseProps } from "..";
import Button from "../../Button";
import Tooltip from "../../Tooltip";
// css
import css from "./TruncatedText.module.scss";
import { TypographySizeMap } from "./internal/TruncatedTextUtils";
// hooks
import { useTruncatedText } from "./internal/useTruncatedText";

declare interface TruncatedTextProps extends BaseProps {
	/**
	 * Text to truncate.
	 */
	text: string;
	/**
	 * maximum width of field in pixels, will truncate anything after this width
	 */
	maxWidth: number;
	/**
	 * Number of lines to truncate to.
	 * @default 1
	 */
	maxLines?: number;
	/**
	 * Suffix to append to truncated text.
	 * @default "..."
	 */
	truncationSuffix?: string;
	/**
	 * should have a tooltip for truncated text
	 * @default true
	 */
	useTooltip?: "never" | "always" | "on-truncate";
	/**
	 * Placement of tooltip.
	 * @default "top"
	 */
	tooltipPlacement?: "top" | "top-start" | "top-end" | "bottom" | "bottom-start" | "bottom-end" | "left" | "right";
	/**
	 * Tooltip Revealed property
	 * @default false
	 */
	revealed?: boolean;

	/**
	 * Positioning strategy for Popper.js to use. Use "fixed" to position if is being clipped by parent container.
	 * https://popper.js.org/docs/v2/constructors/#strategy
	 * @default "absolute"
	 */
	strategy?: "absolute" | "fixed";

	/**
	 * Word break strategy for text.
	 * @default "break-all"
	 */
	wordBreakStrategy?: "break-all" | "break-word";

	/**
	 * If true, show expand button
	 * @default false
	 */
	canShowMore?: boolean;

	/**
	 * Label for expand button
	 * @default "Show More"
	 */
	expandButtonLabelOpen?: string;

	/**
	 * Label for expand button
	 * @default "Show Less"
	 */
	expandButtonLabelClose?: string;

	/**
	 * Average Number of characters to break on
	 */
	lineBreakCharactersAmount?: number;

	/**
	 * className for the component
	 */
	className?: string;
}
/**
 * @deprecated Use the py-line-clamp-* classes instead.
 */
const TruncatedText = (props: TruncatedTextProps) => {
	const {
		text,
		size,
		weight,
		maxWidth,
		maxLines = 1,
		useTooltip = "on-truncate",
		tooltipPlacement = "top",
		truncationSuffix = "...",
		revealed = false,
		strategy = "absolute",
		wordBreakStrategy = "break-all",
		expandButtonLabelOpen = "Show More",
		expandButtonLabelClose = "Show Less",
		canShowMore = false,
		lineBreakCharactersAmount = 1,
		className,
	} = props;
	const [showMore, setShowMore] = React.useState(false);

	const offsetFromLineBreak = (maxLines - 1) * TypographySizeMap[size].width * lineBreakCharactersAmount;

	const { truncatedText, fullTextWidth, didTruncateText } = useTruncatedText(
		text,
		maxWidth * maxLines - offsetFromLineBreak,
		size,
		weight,
		truncationSuffix
	);

	const [displayText, setDisplayText] = React.useState(truncatedText);
	const textLineHeight = TypographySizeMap[size].height;

	const totalLines = Math.ceil(fullTextWidth / maxWidth);

	useEffect(() => {
		if (!showMore) {
			setDisplayText(truncatedText);
		}
	}, [truncatedText]);

	useEffect(() => {
		if (showMore) {
			setDisplayText(text);
		}
	}, [showMore]);

	const springs = useSpring({
		maxHeight: showMore ? `${textLineHeight * totalLines}px` : `${textLineHeight * maxLines}px`,
		onRest: () => {
			if (!showMore) {
				setDisplayText(truncatedText);
			}
		},
	});

	if (didTruncateText === null) {
		return <div className={css.loadingCell} />;
	}

	return (
		<Tooltip
			message={text}
			maxWidth={maxWidth}
			disabled={useTooltip === "never" || (useTooltip === "on-truncate" && !didTruncateText)}
			placement={tooltipPlacement}
			strategy={strategy}
			revealed={revealed}
		>
			<animated.div
				className={`${css.truncatedText} ${className ?? null}`}
				style={{ maxWidth: maxWidth, wordBreak: wordBreakStrategy, ...springs }}
			>
				<Text {...props} size={size} weight={weight}>
					{displayText}
				</Text>
			</animated.div>

			{didTruncateText && canShowMore && (
				<Button
					className={css.truncatedTextButton}
					color="gray"
					size="micro"
					onClick={() => setShowMore(!showMore)}
				>
					{showMore ? expandButtonLabelClose : expandButtonLabelOpen}
				</Button>
			)}
		</Tooltip>
	);
};

/** @deprecated Use the py-line-clamp-* classes instead. */
export default TruncatedText;
